import React, { FC, useCallback } from 'react';
import { XIcon } from '@components-library/Icons/iconsComponents';
import ModalPortal from '@components-shared/modal/ModalPortal';
import { useModalEventObserver } from '@components-shared/modal/modalEventObserver';
import { keyMap, KeyMapKeys } from '@locales/lokalise/keyMap';
import cn from 'classnames';
import i18next from 'i18next';

const ESC_KEY = 'Escape';

export interface IModalProps {
  rootClassName?: string;
  closeHandler?: () => void;
  children?: React.ReactNode;
  showModalCloseButton?: boolean;
  title?: string;
  buttonTitle?: string;
  subtitle?: string;
  header?: React.ReactNode;
  footer?: boolean;
  modalFooter?: React.ReactNode[];
  notification?: JSX.Element;
  titleTranslationOption?: Record<string, unknown>;
  closeOnEscKey?: boolean;
  classNameContainer?: string;
  classNameHeader?: string;
  classNameFooter?: string;
  classNameBody?: string;
  classNameCloseButton?: string;
}

export const Modal: FC<IModalProps> = ({
  closeOnEscKey = true,
  closeHandler,
  rootClassName,
  children,
  showModalCloseButton = true,
  title,
  buttonTitle,
  subtitle,
  footer = false,
  header,
  modalFooter,
  notification,
  titleTranslationOption,
  classNameContainer,
  classNameFooter,
  classNameHeader,
  classNameBody,
  classNameCloseButton,
}) => {
  const close = useCallback(
    (event: React.MouseEvent | Event): void => {
      event?.stopPropagation?.();
      closeHandler?.();
    },
    [closeHandler],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      if (event.key === ESC_KEY && closeOnEscKey) {
        close(event);
      }
    },
    [closeOnEscKey, close],
  );

  useModalEventObserver(handleKeyDown, { ignoreEscButton: !closeOnEscKey });

  const titleOptions = titleTranslationOption ? titleTranslationOption : undefined;
  const titleTranslation = title && (
    // @ts-ignore
    <div className="modal__title">{i18next.t(title as any, titleOptions as any)}</div>
  );

  return (
    <ModalPortal>
      <div className={cn('modal', rootClassName)}>
        <div className={cn('modal__container', classNameContainer)}>
          <div className={cn('modal__header', classNameHeader)}>
            {header && <div className="modal__header__outerHeader">{header}</div>}
            {titleTranslation}
            {subtitle && <div className="modal__subtitle">{i18next.t(subtitle as KeyMapKeys)}</div>}
            {showModalCloseButton && (
              <button type="button" className={cn('modal__close', classNameCloseButton)} onClick={close}>
                <XIcon />
              </button>
            )}
          </div>
          {notification}
          <div className={cn('modal__body', classNameBody)}>{children}</div>

          {footer && (
            <div className={cn('modal__footer', classNameFooter)}>
              <button type="button" className="btn" onClick={close}>
                {i18next.t(buttonTitle ? (buttonTitle as KeyMapKeys) : (keyMap.modalClose as KeyMapKeys))}
              </button>
            </div>
          )}

          {modalFooter && <div className={cn('modal__footer', classNameFooter)}>{modalFooter}</div>}
        </div>
      </div>
    </ModalPortal>
  );
};
